// frontend/src/charts/matrix.js
// The matrix visual's shape (Phase 3 step 7e-2, decision D12). Pure, shared
// by the app's MatrixView and, from step 10b, the embed.
//
// The server (7e-1) returns one row per grouping set: detail cells, every
// subtotal and the grand total, each marked by the trailing "_totals" column
// (a bit per dimension, the first dimension the highest bit, 1 where that
// dimension is rolled up). This module turns those rows into:
//   * a tree of row groups (a node per row-field value, its own cells holding
//     the group's subtotals, the leaves holding the detail cells),
//   * the column headers (distinct column-field values, capped at
//     MAX_HEADERS, with a Total column per group and overall), and
//   * a flat list of visible rows for a given set of collapsed groups.
// Nothing is added up here: every number shown is one the database computed.
import { dimensionText } from './format';

export const MAX_HEADERS = 50;
export const BLANK = '(blank)';
export const TOTAL = 'Total';
// Marks a rolled-up column field inside a column key (never a real value).
const ROLLED = '\u0000rolled';

const keyOf = (values) => JSON.stringify(values);
export const rowKey = (path) => JSON.stringify(path);
const text = (v) => (v === null || v === undefined || v === '' ? BLANK : String(v));
// A header or row label: a dated bucket is written like the chart's axis (format.dimensionText); the key stays the raw value.
const label = (v, col) => (v === null || v === undefined || v === '' ? BLANK : dimensionText(v, col));

/** Which result columns play which part, for a layout { rows, columns, totals }. */
export function splitColumns(columns, layout) {
  const dims = columns.filter((c) => c.kind === 'dimension');
  const k = layout.rows;
  const m = layout.columns || 0;
  return {
    rowFields: dims.slice(0, k),
    colFields: dims.slice(k, k + m),
    measures: columns.filter((c) => c.kind === 'measure'),
    totalsColumn: columns.find((c) => c.kind === 'totals') || null,
    dims,
  };
}

/**
 * rows + columns + layout -> the matrix model. `totals` follows the layout
 * (false: no subtotal cells, no Total columns, no grand row).
 */
export function buildMatrix(columns, rows, layout) {
  const parts = splitColumns(columns || [], layout);
  const { rowFields, colFields, measures, totalsColumn, dims } = parts;
  const totals = layout.totals !== false && totalsColumn != null;
  const at = new Map((columns || []).map((c, i) => [c.alias, i]));
  const idx = (c) => at.get(c.alias);
  const n = dims.length;
  const k = rowFields.length;
  const m = colFields.length;
  const rolled = (mask, i) => (mask & (1 << (n - 1 - i))) !== 0;

  const root = { key: rowKey([]), path: [], depth: 0, label: null, children: [], byValue: new Map(), cells: new Map() };
  const headerSeen = new Map();        // detail column key -> { key, values }
  let headerCount = 0;

  for (const r of rows || []) {
    const mask = totalsColumn ? Number(r[idx(totalsColumn)]) || 0 : 0;
    let depth = 0;
    while (depth < k && !rolled(mask, depth)) depth += 1;
    for (let d = depth; d < k; d += 1) if (!rolled(mask, d)) { depth = -1; break; }   // not a hierarchy row
    if (depth < 0) continue;
    if (!totals && depth < k) continue;                                               // a subtotal we were not asked for

    const colValues = colFields.map((c, j) => (rolled(mask, k + j) ? ROLLED : r[idx(c)]));
    const colKey = keyOf(colValues);
    if (m > 0 && !colValues.includes(ROLLED) && !headerSeen.has(colKey)) {
      headerCount += 1;
      if (headerSeen.size >= MAX_HEADERS) continue;          // past the cap: the column is not shown
      headerSeen.set(colKey, { key: colKey, values: colValues });
    }
    if (!totals && colValues.includes(ROLLED)) continue;

    let node = root;
    for (let d = 0; d < depth; d += 1) {
      const v = r[idx(rowFields[d])];
      const vk = text(v);
      let child = node.byValue.get(vk);
      if (!child) {
        const path = [...node.path, vk];
        child = { key: rowKey(path), path, depth: d + 1, label: label(v, rowFields[d]), children: [], byValue: new Map(), cells: new Map() };
        node.byValue.set(vk, child);
        node.children.push(child);
      }
      node = child;
    }
    node.cells.set(colKey, measures.map((mc) => r[idx(mc)]));
  }

  // Leaf columns in display order: under each first-level column value its
  // detail columns then its Total (two column fields), then the overall Total.
  const headers = [...headerSeen.values()];
  const leafColumns = [];
  if (m === 0) {
    leafColumns.push({ key: keyOf([]), labels: [], total: false });
  } else if (m === 1) {
    for (const h of headers) leafColumns.push({ key: h.key, labels: [label(h.values[0], colFields[0])], total: false });
    if (totals) leafColumns.push({ key: keyOf([ROLLED]), labels: [TOTAL], total: true });
  } else {
    const groups = [];
    const byFirst = new Map();
    for (const h of headers) {
      const first = text(h.values[0]);
      if (!byFirst.has(first)) { byFirst.set(first, { label: label(h.values[0], colFields[0]), items: [] }); groups.push(byFirst.get(first)); }
      byFirst.get(first).items.push(h);
    }
    for (const g of groups) {
      for (const h of g.items) leafColumns.push({ key: h.key, labels: [g.label, label(h.values[1], colFields[1])], total: false, group: g.label });
      if (totals) leafColumns.push({ key: keyOf([g.items[0].values[0], ROLLED]), labels: [g.label, TOTAL], total: true, group: g.label });
    }
    if (totals) leafColumns.push({ key: keyOf([ROLLED, ROLLED]), labels: [TOTAL, ''], total: true, group: TOTAL, grand: true });
  }

  return {
    rowFields, colFields, measures, totals, root, leafColumns,
    headerCount, headersTruncated: headerCount > headers.length,
    grand: totals ? root : null,
  };
}

/**
 * The visible rows, top down: a group's row (its subtotals) then, unless its
 * key is in `collapsed`, its children; the grand total last when there is
 * one. Each entry: { node, depth, hasChildren, collapsed, grand }.
 */
export function flattenRows(matrix, collapsed = new Set()) {
  const out = [];
  const walk = (node) => {
    for (const child of node.children) {
      const hasChildren = child.children.length > 0;
      const isCollapsed = hasChildren && collapsed.has(child.key);
      out.push({ node: child, depth: child.depth, hasChildren, collapsed: isCollapsed, grand: false });
      if (hasChildren && !isCollapsed) walk(child);
    }
  };
  walk(matrix.root);
  if (matrix.grand && matrix.grand.cells.size) out.push({ node: matrix.root, depth: 0, hasChildren: false, collapsed: false, grand: true });
  return out;
}

/** Every group key that has children (for "collapse all"). */
export function groupKeys(matrix) {
  const out = [];
  const walk = (node) => { for (const c of node.children) if (c.children.length) { out.push(c.key); walk(c); } };
  walk(matrix.root);
  return out;
}

/** The values a row shows under a leaf column, measure by measure (null when the server gave no such cell). */
export function cellValues(node, leafColumn, measureCount) {
  return node.cells.get(leafColumn.key) || Array.from({ length: measureCount }, () => null);
}